import { Component, computed, input, output, OnInit } from '@angular/core';

import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    searchState,
    updatePagination,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    triggerDataRefresh,
} from '@app/stores/searchStore';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs';
import { untilDestroyed } from '@ngneat/until-destroy';

@Component({
    selector: 'app-table-pagination',
    standalone: true,
    imports: [
        MatPaginatorModule,
        MatIconModule,
        MatButtonModule,
        MatInputModule,
        MatSelectModule,
        MatFormFieldModule,
        MatTooltipModule,
        TranslateModule,
        FormsModule,
        ReactiveFormsModule,
    ],
    templateUrl: './table-pagination.component.html',
    styleUrls: ['./table-pagination.component.scss'],
})
export class TablePaginationComponent implements OnInit {
    // Inputs
    loading = input<boolean>(false);
    pageSizeOptions = input<number[]>([10, 20, 50, 100]);

    // Outputs
    pageChange = output<PageEvent>();

    // Get current search state
    searchData = searchState;

    constructor(private translate: TranslateService) {}

    // Computed values from search state
    currentOffset = computed(() => this.searchData().pagination.offset);
    currentLimit = computed(() => this.searchData().pagination.limit);
    totalItems = computed(() => this.searchData().pagination.totalCount);
    hasMore = computed(() => this.searchData().pagination.hasMore);

    // Computed pagination values
    currentPageSize = computed(() => this.currentLimit());
    currentPageNumber = computed(
        () => Math.floor(this.currentOffset() / this.currentLimit()) + 1
    );
    totalPages = computed(() =>
        Math.max(1, Math.ceil(this.totalItems() / this.currentLimit()))
    );

    isFirstPage = computed(() => this.currentOffset() === 0);
    isLastPage = computed(
        () => !this.hasMore() || this.currentPageNumber() >= this.totalPages()
    );

    getItemsRangeText = computed(() => {
        const offset = this.currentOffset();
        const limit = this.currentLimit();
        const total = this.totalItems();

        if (total === 0) {
            return this.translate.instant('PAGINATION.NO_ITEMS');
        }

        const start = offset + 1;
        const end = Math.min(offset + limit, total);

        return this.translate.instant('PAGINATION.RANGE_TEXT', {
            start: start,
            end: end,
            total: total,
        });
    });

    pageControl: FormControl = new FormControl('1');

    ngOnInit() {
        this.pageControl.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                skip(1) // Skip initial to prevent double fetch on load
            )
            .subscribe(value => {
                this.onPageInputChange(value);
            });

        // Initialize the form control with the actual current page
        // This ensures the display is correct without triggering valueChanges
        this.pageControl.setValue(this.currentPageNumber().toString(), {
            emitEvent: false,
        });
    }

    onPageSizeChange(newPageSize: number): void {
        updatePagination({
            limit: newPageSize,
            offset: 0, // Reset to first page when changing page size
        });

        // Trigger data refresh with new pagination settings
        triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: 0,
            pageSize: newPageSize,
            length: this.totalItems(),
        });
    }

    onPageInputChange(pageNumber: number): void {
        console.log('pageInput changed to:', pageNumber);
        if (pageNumber >= 1 && pageNumber <= this.totalPages()) {
            const newOffset = (pageNumber - 1) * this.currentLimit();
            goToPage(newOffset);

            // Trigger data refresh with new pagination settings
            triggerDataRefresh();

            this.pageChange.emit({
                pageIndex: pageNumber - 1,
                pageSize: this.currentLimit(),
                length: this.totalItems(),
            });
        } else {
            // Reset to current page if invalid
            this.pageControl.patchValue(this.currentPageNumber().toString());
        }
    }

    goToFirstPage(): void {
        goToPage(0);

        // Trigger data refresh with new pagination settings
        triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: 0,
            pageSize: this.currentLimit(),
            length: this.totalItems(),
        });
    }

    goToPrevPage(): void {
        goToPreviousPage();

        // Trigger data refresh with new pagination settings
        triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: this.currentPageNumber() - 2, // -2 because we already went back one page
            pageSize: this.currentLimit(),
            length: this.totalItems(),
        });
    }

    goToNextPageHandler(): void {
        goToNextPage();

        // Trigger data refresh with new pagination settings
        triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: this.currentPageNumber(), // currentPageNumber will be the new page after goToNextPage
            pageSize: this.currentLimit(),
            length: this.totalItems(),
        });
    }

    goToLastPage(): void {
        const lastPageOffset = (this.totalPages() - 1) * this.currentLimit();
        goToPage(lastPageOffset);

        // Trigger data refresh with new pagination settings
        triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: this.totalPages() - 1,
            pageSize: this.currentLimit(),
            length: this.totalItems(),
        });
    }
}
